import React, { useMemo } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useHolidays, useMyApprovedLeaveDays } from '../../hooks/useLeave';
import type { LeaveType } from '../../api/leave.api';

const TYPE_LABEL: Record<LeaveType, string> = {
  legal: 'Congé légal',
  recup: 'Récupération',
  unpaid: 'Sans solde',
  sick: 'Maladie',
};

const DAY_NAMES_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function chipDate(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = (dt.getUTCDay() + 6) % 7;
  return `${DAY_NAMES_SHORT[dow]}. ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
}

function addDays(iso: string, n: number) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

export const SpecialDaysBanner: React.FC<{ weekStart: string; isEmployee?: boolean }> = ({ weekStart, isEmployee = false }) => {
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const { data: holidays = [] } = useHolidays({ from: weekStart, to: weekEnd });
  const { data: leaves = [] } = useMyApprovedLeaveDays({ from: weekStart, to: weekEnd });

  const inRange = (date: string) => date >= weekStart && date <= weekEnd;
  const hols = holidays.filter(h => inRange(h.date));
  const leas = leaves.filter(l => inRange(l.date));

  if (hols.length === 0 && leas.length === 0) return null;

  return (
    <div className="pl-rd-special">
      <div className="pl-rd-special-icon-circle">
        <CalendarDaysIcon />
      </div>
      <div className="pl-rd-special-content">
        <div className="pl-rd-special-row">
          <h3 className="pl-rd-special-title">Jours particuliers cette semaine</h3>
          {hols.map(h => (
            <span key={`h-${h.id}`} className="pl-rd-chip pl-rd-chip--orange">
              {chipDate(h.date)} – Férié
            </span>
          ))}
          {leas.map(l => (
            <span key={`l-${l.request_id}-${l.date}`} className="pl-rd-chip pl-rd-chip--blue">
              {chipDate(l.date)} – {TYPE_LABEL[l.type]}{l.fraction < 1 ? ' ½' : ''}
            </span>
          ))}
        </div>
        <p className="pl-rd-special-desc">
          {isEmployee ? (
            <>
              Les fériés sont automatiquement vidés de votre horaire et comptés à
              votre base journalière — vous n'avez rien à faire. Si vous travaillez
              réellement un férié, activez le jour et encodez vos heures : elles
              s'ajoutent au crédit du férié (repos compensateur).{' '}
            </>
          ) : (
            <>
              En tant qu'indépendant(e), les fériés ne créditent pas d'heures :
              votre horaire habituel reste encodé et seules les heures réellement
              prestées comptent. Si vous ne travaillez pas un férié, désactivez le
              jour ou posez un congé.{' '}
            </>
          )}
          Les congés récup et sans solde débitent le compteur ; pour les congés
          payés et maladie, laissez votre horaire habituel encodé.
        </p>
      </div>
    </div>
  );
};
